import type { HabitSchedule } from '../types';
import { scheduleMissingDays } from './habitSchedule';
import { askHabitDays } from '../stores/habitDaysRequestStore';

/**
 * Ask — one dialog at a time — for the days of every habit that has none.
 *
 * Returns the schedules in the SAME ORDER as `habits` (unchanged ones as given), or
 * `null` as soon as the person cancels one: the caller then creates NOTHING and
 * charges NOTHING. ⚠️ Call it BEFORE any `spendCoin` or capacity commit.
 */
export async function pickMissingDays(
  habits: Array<{ name: string; schedule: HabitSchedule }>,
): Promise<HabitSchedule[] | null> {
  const out: HabitSchedule[] = [];
  for (const h of habits) {
    if (!scheduleMissingDays(h.schedule)) { out.push(h.schedule); continue; }
    const picked = await askHabitDays(h.name, h.schedule);
    if (!picked || scheduleMissingDays(picked)) return null;
    out.push(picked);
  }
  return out;
}

type HabitNode = { kind: 'habit'; name?: string; schedule: HabitSchedule };
const isDaylessHabit = (v: unknown): v is HabitNode =>
  !!v && typeof v === 'object' && (v as HabitNode).kind === 'habit' &&
  !!(v as HabitNode).schedule && scheduleMissingDays((v as HabitNode).schedule);

/**
 * A shared payload (habit, routine, quest, program …) with the days picked for every
 * habit inside it that has none — or `null` if the person cancelled. Walks the whole
 * payload, so a habit nested in a routine inside a program is found too. The input
 * is never mutated; a payload with nothing missing comes back as the same object.
 */
export async function payloadWithDaysPicked<P>(payload: P): Promise<P | null> {
  const found: HabitNode[] = [];
  const walk = (v: unknown) => {
    if (Array.isArray(v)) { v.forEach(walk); return; }
    if (!v || typeof v !== 'object') return;
    if (isDaylessHabit(v)) found.push(v);
    Object.values(v).forEach(walk);
  };
  walk(payload);
  if (found.length === 0) return payload;

  const picked = await pickMissingDays(found.map(h => ({ name: h.name || 'This habit', schedule: h.schedule })));
  if (!picked) return null;

  const replacement = new Map<HabitNode, HabitSchedule>(found.map((h, i) => [h, picked[i]]));
  const copy = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(copy);
    if (!v || typeof v !== 'object') return v;
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v)) out[k] = copy(val);
    const schedule = replacement.get(v as HabitNode);
    if (schedule) out.schedule = schedule;
    return out;
  };
  return copy(payload) as P;
}
